/**
 * Jupiter API Service - Solana DEX Aggregator
 *
 * Jupiter aggregates liquidity from Solana DEXes like Raydium, Orca, etc.
 * Used for Solana ↔ Solana swaps.
 *
 * API endpoint and token mints switch between mainnet/devnet via networkConfig.
 */

import { getJupiterConfig } from './networkConfig';

function getApiBase(): string {
  return getJupiterConfig().apiBase;
}

// Dynamic token lookup that reflects current network
export const SOLANA_TOKENS: Record<string, string> = new Proxy({} as Record<string, string>, {
  get(_target, prop: string) {
    return getJupiterConfig().tokens[prop];
  },
  ownKeys() {
    return Object.keys(getJupiterConfig().tokens);
  },
  getOwnPropertyDescriptor(_target, prop: string) {
    const tokens = getJupiterConfig().tokens;
    if (prop in tokens) {
      return { configurable: true, enumerable: true, value: tokens[prop] };
    }
    return undefined;
  },
  has(_target, prop: string) {
    return prop in getJupiterConfig().tokens;
  },
});

export interface JupiterQuoteRequest {
  inputMint: string;
  outputMint: string;
  amount: string; // In smallest unit (lamports for SOL)
  slippageBps?: number; // Slippage in basis points (50 = 0.5%)
  swapMode?: 'ExactIn' | 'ExactOut';
  onlyDirectRoutes?: boolean;
  asLegacyTransaction?: boolean;
}

export interface JupiterRoutePlan {
  swapInfo: {
    ammKey: string;
    label: string;
    inputMint: string;
    outputMint: string;
    inAmount: string;
    outAmount: string;
    feeAmount: string;
    feeMint: string;
  };
  percent: number;
}

export interface JupiterQuote {
  inputMint: string;
  inAmount: string;
  outputMint: string;
  outAmount: string;
  otherAmountThreshold: string;
  swapMode: string;
  slippageBps: number;
  priceImpactPct: string;
  routePlan: JupiterRoutePlan[];
  contextSlot?: number;
  timeTaken?: number;
}

export interface JupiterSwapRequest {
  quoteResponse: JupiterQuote;
  userPublicKey: string;
  wrapAndUnwrapSol?: boolean;
  useSharedAccounts?: boolean;
  feeAccount?: string;
  computeUnitPriceMicroLamports?: number;
  asLegacyTransaction?: boolean;
  useTokenLedger?: boolean;
  destinationTokenAccount?: string;
  dynamicComputeUnitLimit?: boolean;
  skipUserAccountsRpcCalls?: boolean;
  prioritizationFeeLamports?: number | 'auto';
}

export interface JupiterSwapResponse {
  swapTransaction: string; // Base64 encoded transaction
  lastValidBlockHeight: number;
  prioritizationFeeLamports?: number;
}

/**
 * Get token mint address from symbol (reads from current network config)
 */
export function getSolanaMint(symbol: string, contractAddress?: string): string | null {
  if (contractAddress) {
    return contractAddress;
  }
  return getJupiterConfig().tokens[symbol] || null;
}

class JupiterApiService {
  /**
   * Get a swap quote from Jupiter
   */
  async getQuote(request: JupiterQuoteRequest): Promise<JupiterQuote> {
    const params = new URLSearchParams({
      inputMint: request.inputMint,
      outputMint: request.outputMint,
      amount: request.amount,
      slippageBps: (request.slippageBps || 50).toString(),
      swapMode: request.swapMode || 'ExactIn',
    });

    if (request.onlyDirectRoutes) {
      params.append('onlyDirectRoutes', 'true');
    }
    if (request.asLegacyTransaction) {
      params.append('asLegacyTransaction', 'true');
    }

    const response = await fetch(`${getApiBase()}/quote?${params}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to get Jupiter quote: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get the swap transaction from Jupiter
   */
  async getSwapTransaction(request: JupiterSwapRequest): Promise<JupiterSwapResponse> {
    const body = {
      quoteResponse: request.quoteResponse,
      userPublicKey: request.userPublicKey,
      wrapAndUnwrapSol: request.wrapAndUnwrapSol ?? true,
      useSharedAccounts: request.useSharedAccounts ?? true,
      dynamicComputeUnitLimit: request.dynamicComputeUnitLimit ?? true,
      prioritizationFeeLamports: request.prioritizationFeeLamports ?? 'auto',
    };

    const response = await fetch(`${getApiBase()}/swap`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to get Jupiter swap tx: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get quote with simplified parameters
   */
  async getSimpleQuote(
    fromSymbol: string,
    toSymbol: string,
    amount: string,
    slippagePercent: number = 0.5,
    fromContract?: string,
    toContract?: string
  ): Promise<JupiterQuote> {
    const inputMint = getSolanaMint(fromSymbol, fromContract);
    const outputMint = getSolanaMint(toSymbol, toContract);

    if (!inputMint) {
      throw new Error(`Unknown Solana token: ${fromSymbol}`);
    }
    if (!outputMint) {
      throw new Error(`Unknown Solana token: ${toSymbol}`);
    }

    return this.getQuote({
      inputMint,
      outputMint,
      amount,
      slippageBps: Math.floor(slippagePercent * 100),
    });
  }
}

export const jupiterApi = new JupiterApiService();
export { JupiterApiService };
